import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Receipt, Trash2, TrendingDown, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { useTeamPermissions } from '../contexts/TeamPermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Expenses = () => {
  const { t } = useTranslation();
  const { canEdit } = useTeamPermissions();
  const [expenses, setExpenses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentStatus, setDocumentStatus] = useState({});
  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    category: '',
    package_id: '',
    date: new Date().toISOString().split('T')[0],
  });

  const categories = [
    { key: 'marketing', label: t('expenses.marketing') },
    { key: 'office', label: t('expenses.office') },
    { key: 'travel', label: t('expenses.travel') },
    { key: 'software', label: t('expenses.software') },
    { key: 'professionalServices', label: t('expenses.professionalServices') },
    { key: 'utilities', label: t('expenses.utilities') },
    { key: 'insurance', label: t('expenses.insurance') },
    { key: 'miscellaneous', label: t('expenses.miscellaneous') }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [expensesRes, packagesRes] = await Promise.all([
        axios.get(`${API}/expenses`, { headers }),
        axios.get(`${API}/packages`, { headers })
      ]);
      
      setExpenses(expensesRes.data);
      setPackages(packagesRes.data);
      
      // Fetch document status for each expense
      const statusPromises = expensesRes.data.map(expense =>
        axios.get(`${API}/expenses/${expense.expense_id}/document-status`, { headers })
          .then(res => ({ expense_id: expense.expense_id, status: res.data }))
          .catch(() => ({ expense_id: expense.expense_id, status: {} }))
      );
      
      const statuses = await Promise.all(statusPromises);
      const statusMap = {};
      statuses.forEach(s => {
        statusMap[s.expense_id] = s.status;
      });
      setDocumentStatus(statusMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('expenses.expenseAdded'));
      setDialogOpen(false);
      setNewExpense({
        description: '',
        amount: '',
        category: '',
        package_id: '',
        date: new Date().toISOString().split('T')[0],
      });
      fetchData();
    } catch (error) {
      toast.error(t('errors.failedToSave'));
    }
  };

  const handleDelete = async (expenseId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('expenses.expenseDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.failedToDelete'));
    }
  };

  const handleArchive = async (expenseId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.patch(`${API}/expenses/${expenseId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('expenses.expenseArchived'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">{t('expenses.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('expenses.subtitle')}</p>
        </div>
        {canEdit('expenses') && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t('expenses.addExpense')}
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('expenses.addNewExpense')}</DialogTitle>
              <DialogDescription>{t('expenses.recordExpense')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">{t('common.description')} *</Label>
                <Textarea
                  id="description"
                  placeholder={t('expenses.descriptionPlaceholder')}
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  required
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">{t('expenses.descriptionHelp')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">{t('common.amount')} (€) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">{t('common.date')} *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newExpense.date}
                    onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">{t('expenses.category')} *</Label>
                <Select
                  value={newExpense.category}
                  onValueChange={(value) => setNewExpense({ ...newExpense, category: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('expenses.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.key} value={cat.label}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="package">{t('expenses.linkToPackageOptional')}</Label>
                <Select
                  value={newExpense.package_id || "none"}
                  onValueChange={(value) => setNewExpense({ ...newExpense, package_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('expenses.noPackage')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('expenses.noPackage')}</SelectItem>
                    {packages.map((pkg) => (
                      <SelectItem key={pkg.package_id} value={pkg.package_id}>
                        {pkg.package_id} - {pkg.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full">{t('expenses.addExpense')}</Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Summary Card */}
      <Card className="bg-destructive/5 border-destructive/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-destructive/10 rounded-full">
              <TrendingDown className="w-6 h-6 text-destructive" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('expenses.totalExpenses')}</p>
              <p className="text-3xl font-bold text-destructive">€{totalExpenses.toFixed(2)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            {t('expenses.expenseRecords')}
          </CardTitle>
          <CardDescription>{t('expenses.allExpensesOutside')}. {t('expenses.useExpenseId')}</CardDescription>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('expenses.noExpenses')}</p>
              <p className="text-sm mt-2">{t('expenses.createFirst')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('expenses.expenseId')}</TableHead>
                  <TableHead>{t('common.date')}</TableHead>
                  <TableHead>{t('common.description')}</TableHead>
                  <TableHead>{t('expenses.category')}</TableHead>
                  <TableHead className="text-right">{t('common.amount')}</TableHead>
                  <TableHead>{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.expense_id}>
                    <TableCell className="font-mono text-sm">{expense.expense_id}</TableCell>
                    <TableCell>{expense.date}</TableCell>
                    <TableCell className="max-w-xs truncate">{expense.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{expense.category}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">€{expense.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {canEdit('expenses') && (
                          <>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleArchive(expense.expense_id)}
                              title={t('common.archive')}
                            >
                              <Archive className="w-4 h-4 text-muted-foreground" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleDelete(expense.expense_id)}
                              title={t('common.delete')}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Expenses;
